import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ApplicationState } from '../store';
import { AvailableTeam, TeamRole, UserContext } from '../store/Models/UserContext';
import { ToxnotComponent, ToxnotComponentProperties } from './core/BaseApplicationComponents/ToxnotComponent';

import { ToxnotReactComponentPaths } from './core/ToxnotReactComponentPaths';
import { CreateAndEditSurveyComponentPaths } from './Survey/SurveyComponentPaths';
import TOW from './TOW';
import * as LoginStateController from '../store/StateControllers/LoginStateController';

import './Layout.css';
import { UpsellProBadge } from './core/Upsell';
import ThreeEAppStrip from './ThreeEAppStrip/ThreeEAppStrip';

interface NavMenuLinkProps {
    navItemClass: string;
    href: string;
    isActive: boolean;
    iconSrc: string;
    localizedText: string;
    lastNavItemInGroup: boolean;
    userRole: TeamRole;
    menuItem_onClick?: (e: React.MouseEvent) => void;
    hide?: boolean;
    isUpsell?: boolean;
    context?: string; // For legacy testing
    navGroupId?: string; // For legacy testing
    hiddenForRoles?: TeamRole[];
    isActiveParent?: boolean;
    isSideNavCollapsed?: boolean;
}

// ===============================================================================

/**
 * Base class for rendering a simple nav link
 */
abstract class NavMenuLinkBase<P extends NavMenuLinkProps> extends React.PureComponent<P> {

    // ===============================================================================

    public render() {
        if (this.props.hiddenForRoles !== undefined) {
            if (this.props.hiddenForRoles.some(role => role === this.props.userRole)) {
                return null;
            }
        }

        return (
            <li className={this.props.navItemClass} style={this.getLiStyles()} data-nav-group-id={this.props.navGroupId ? this.props.navGroupId : ""}>
                {this.getInnerLinkContents()}
            </li>
        );
    }

    // ===============================================================================

    protected abstract getInnerLinkContents(): JSX.Element;

    // ===============================================================================

    private getMarginBottomIfLastElement(lastItemInNavGroup: boolean): React.CSSProperties {
        return lastItemInNavGroup ? { marginBottom: 15 } : {};
    }

    // ===============================================================================

    private getLiStyles() {
        const liStyles: React.CSSProperties = this.getMarginBottomIfLastElement(this.props.lastNavItemInGroup);

        if (this.props.hide) {
            liStyles.display = "none";
        } else if (this.props.isActiveParent && !this.props.isSideNavCollapsed) {
            liStyles.display = "list-item";
        }

        return liStyles;
    }

    // ===============================================================================

    protected renderNavMenuLinkIconAndContent(): JSX.Element {
        return (
            <>
                <div className="icon">
                    {this.props.iconSrc !== "" && <img src={(this.props.isActive ? "/images/nav/" : "/images/nav/gray/") + this.props.iconSrc} />}
                </div>
                <div className="content">
                    {this.props.localizedText}
                </div>
                {this.props.isUpsell && <UpsellProBadge />}
            </>
        );
    }
}

// ===============================================================================

/**
 * Renders a simple nav menu link for navigation
 */
class NavMenuLink extends NavMenuLinkBase<NavMenuLinkProps> {

    // ===============================================================================

    protected getInnerLinkContents(isNavbarCollapsed: boolean = false) {
        const attributes: { [attribute: string]: string } = {};

        if (this.props.menuItem_onClick) {
            attributes["role"] = "button";
        }
        else {
            attributes["href"] = this.props.href;
        }

        const iconAndContent = this.renderNavMenuLinkIconAndContent();

        return (
            <a data-container=".sidebar-line" data-placement="right" data-toggle="tooltip" title={this.props.localizedText} {...attributes} className={this.props.isActive ? "active" : ""} onClick={e => this.props.menuItem_onClick && this.props.menuItem_onClick(e)} data-context={this.props.context ? this.props.context : ""}>
                {iconAndContent}
            </a>
        );
    }
}


interface NavMenuReactLinkProps extends NavMenuLinkProps {
    smallNav: boolean;
}

// ===============================================================================

/**
 * Renders a simple nav menu link for navigation. The link rendered is a React <Link>
 * and not a regular <a>
 */
class NavMenuReactLink extends NavMenuLinkBase<NavMenuReactLinkProps> {

    // ===============================================================================

    protected getInnerLinkContents() {
        const iconAndContent = this.renderNavMenuLinkIconAndContent();

        return (
            <Link data-container=".sidebar-line" data-placement="right" data-toggle="tooltip" title={this.props.localizedText} to={this.props.href} className={this.props.isActive ? "active" : ""} onClick={e => this.link_click()}>
                {iconAndContent}
            </Link>
        );
    }

    // ===============================================================================

    private link_click = () => {
        if (this.props.smallNav) {
            $("#tn-app-small-navigation-container").toggle();
        }
    }
}


// ===============================================================================

//
// These are options that control the layout. An options object can
// be specified as a Layout component property.
//

export interface LayoutOptions {
    appBodyContentShouldTakeFullWidth?: boolean; // defaults to false
    pinAppContentToWindowHeight?: boolean; // defaults to false
}

// ===============================================================================

//
// These are properties on the Layout component that can be specified in JSX.
// They aren't part of redux state.
//

interface LayoutOwnProps {
    children: React.ReactNode | React.ReactNode[];
    options?: LayoutOptions;
    modalsRender?: () => JSX.Element;
}

// ===============================================================================

type LayoutProps =
    ToxnotComponentProperties &
    LayoutOwnProps &
    typeof LoginStateController.actionCreators;

interface LocalState {
    isSideNavCollapsed: boolean;
    countdown: string;
    isRefreshImminent: boolean;
}

class Layout extends ToxnotComponent<LayoutProps, LocalState> {
    private readonly s_sideNavbarCollapsedCookieName = "toxnot-side-navbar-collapsed";
    private timer: any;

    public constructor(props: LayoutProps) {
        super(props);

        const isCollapsed = this.isTruthyCookie(this.s_sideNavbarCollapsedCookieName);

        this.state = {
            isSideNavCollapsed: isCollapsed,
            countdown: "",
            isRefreshImminent: false
        };
    }

    public componentDidMount() {
        $('[data-toggle="tooltip"]').tooltip();
        //react events are not triggering the tooltip to be displayed
        $('.btn-expand-nav').on("hover", e => $(e).tooltip('show'));
        $('.btn-collapse-nav').on("hover", e => $(e).tooltip('show'));

        document.addEventListener("keydown", (e) => this.navbarKeysEventListener(e, this.navbarCollapse));

        if (!this.props.appServices.config.isProductionServer && this.props.appServices.config.dataRefreshStartDate) {
            this.timer = setInterval(() => this.updateCountdown(), 1000);
            this.updateCountdown();
        }
    }

    public componentWillUnmount() {
        clearInterval(this.timer);
    }
    
    // ===============================================================================

    private hasSubscription = () => this.props.appServices.userContext?.isSubscriptionActive ?? false;

    public render() {
        //
        // Fetch the layout options from the props and apply defaults to un-set options
        //

        const layoutOptionsToUse = { ...(this.props.options ?? {}) };

        layoutOptionsToUse.appBodyContentShouldTakeFullWidth ??= false;
        layoutOptionsToUse.pinAppContentToWindowHeight ??= false;

        const isLoggedIn = null !== this.props.appServices.userContext;
        const threeEAppStripConfiguration = this.props.appServices.threeEAppStrip;
        const showThreeEAppStrip =
            isLoggedIn &&
            threeEAppStripConfiguration.enabled;

        //
        // Render the main layout
        //
        return (
            <React.Fragment>
                <div className={"tn-app-root" + (!this.state.isSideNavCollapsed ? " navbar-expanded" : "")}>
                    {!this.props.appServices.config.isProductionServer && this.props.appServices.config.dataRefreshStartDate &&
                        <div id="uat-banner" style={this.getBannerStyle()}>
                            <span style={{ color: this.state.isRefreshImminent ? 'white' : 'inherit' }}>You're in the Exchange testing environment. Work done here won't appear in your live account.</span>
                            <span style={{ color: this.state.isRefreshImminent ? 'white' : 'inherit' }}>
                                Next data refresh in: <span id="countdown-timer" style={{ color: 'yellow' }}>{this.state.countdown}</span>
                            </span>
                        </div>
                    }
                    <div className="tn-app-container">
                        <header className="tn-app-header">
                            <div className={"tn-app-header-body" + (layoutOptionsToUse.appBodyContentShouldTakeFullWidth ? " full-width" : "")}>
                                {showThreeEAppStrip ?
                                    <div className="tn-app-header-brand three-e-app-strip-enabled">
                                        <ThreeEAppStrip />
                                        <a className="tn-app-logo" href={this.hasSubscription() ? "/Data/Library" : ToxnotReactComponentPaths.StartPath}><img src={"/images/homepage/3e-exchange-logo-color-rgb.svg"} /></a>
                                    </div> :
                                    <a className="tn-app-logo" href={this.hasSubscription() ? "/Data/Library" : ToxnotReactComponentPaths.StartPath}><img src={"/images/homepage/3e-exchange-logo-color-rgb.svg"} /></a>
                                }
                                <nav id="tn-header-nav">
                                    <ul>
                                        <li>
                                            <button type="button" id="tn-sm-nav-expand-button" className="navbar-toggle" onClick={e => this.smallNavExpandButton_click()}>
                                                <span className="sr-only">Toggle navigation</span>
                                                <span className="icon-bar"></span>
                                                <span className="icon-bar"></span>
                                                <span className="icon-bar"></span>
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                            <div id="tn-app-small-navigation-container" className="tn-app-nav-container">
                                {isLoggedIn ? this.renderNavLayout(this.props.appServices.userContext!, true, false) : this.renderSignedOutNavLayout(true)}
                            </div>
                        </header >
                        <div className={"tn-app-sidebar tn-app-nav-container" + (this.state.isSideNavCollapsed ? " collapsed-navbar side-nav-transition" : "")}>
                            <div className="sidebar-min-width">
                                {showThreeEAppStrip ?
                                    <div className="sidebar-logo three-e-app-strip-enabled">
                                        <ThreeEAppStrip />
                                        <a href={process.env.PUBLIC_URL}><img src={"/images/homepage/3e-exchange-logo-color-rgb.svg"} /></a>
                                    </div> :
                                    <div className="sidebar-logo"><a href={process.env.PUBLIC_URL}><img src={"/images/homepage/3e-exchange-logo-color-rgb.svg"} /></a></div>
                                }

                                {isLoggedIn ? this.renderNavLayout(this.props.appServices.userContext!, false, this.state.isSideNavCollapsed) : this.renderSignedOutNavLayout(false)}
                            </div>
                        </div>
                        <div className="tn-app-content-container p-relative p-l-15">
                            <div className="sidebar-line">
                            </div>
                            <button onClick={() => this.navbarCollapse(true)}
                                className={"btn btn-collapse-nav btn-nav btn-round float-right" + (this.state.isSideNavCollapsed ? " initially-hidden" : "")} data-html="true"
                                data-placement="right" data-toggle="tooltip"
                                title="<div>Collapse<div class='tooltip-shortcut-square'>[</div></div>">
                                <span className="glyphicon glyphicon-chevron-left"></span>
                            </button>
                            <button onClick={() => this.navbarCollapse(false)}
                                className={"btn btn-expand-nav btn-nav btn-round float-left" + (!this.state.isSideNavCollapsed ? " initially-hidden" : "")}
                                data-html="true" data-placement="right" data-toggle="tooltip"
                                title="<div>Expand<div class='tooltip-shortcut-square'>]</div></div>">
                                <span className="glyphicon glyphicon-chevron-right"></span>
                            </button>
                            {isLoggedIn && <div id="tow-root" className={layoutOptionsToUse.appBodyContentShouldTakeFullWidth ? "full-width" : ""}>
                                <TOW />
                            </div>}
                            <div className={"tn-app-content-view-scroll-wrapper" + (layoutOptionsToUse.appBodyContentShouldTakeFullWidth ? " full-width" : "")}>
                                <div className={"tn-app-content-view" + (layoutOptionsToUse.pinAppContentToWindowHeight ? " window-height" : "")}>
                                    <div id="tn-app-sections-container">
                                        {this.props.children}
                                    </div>
                                    <div id="tn-tow-inception-full-page-modal-container" className="initially-hidden"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div id="account-menu-content" className="collapsed">
                        <div>
                            {isLoggedIn && this.renderTeamMenu(this.props.appServices.userContext!)}
                        </div>
                    </div>
                </div>
                <div id="modals-root">
                    <div id="tow-decision-modal" className="modal fade" tabIndex={-1} role="dialog">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <button type="button" className="close" data-dismiss="modal" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                                    <h4 id="tow-decision-modal-title" className="modal-title">Confirmation</h4>
                                </div>
                                <div id="tow-decision-modal-body" className="modal-body">
                                    {this.localizeString("Are you sure you want to close this workflow?")}
                                </div>
                                <div className="alert alert-warning initially-hidden">

                                </div>
                                <div className="modal-footer">
                                    <button id="tow-decision-modal-cancel" type="button" className="btn btn-default" data-dismiss="modal">{this.localizeString("Cancel")}</button>
                                    <button id="tow-decision-modal-ok" type="button" className="btn btn-toxnot-primary" onClick={this.tow_decision_modal_ok_onClick}>{this.localizeString("Ok")}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {this.props.modalsRender ? this.props.modalsRender() : null}
                </div>
            </React.Fragment>
        );
    }

    // ===============================================================================

    private isExchangeNavMenuItemActive() {
        const path = window.location.pathname.toLowerCase();

        return path.startsWith(ToxnotReactComponentPaths.BaseSharedMaterialsPath.toLowerCase()) &&
            !this.pathMatches(ToxnotReactComponentPaths.CompanyPagesManager) &&
            !this.pathMatches(ToxnotReactComponentPaths.MyPassports) &&
            !path.includes("/start");
    }

    // ===============================================================================

    private isAnyExchangeNavMenuItemActive() {
        const path = window.location.pathname.toLowerCase();

        return (path.startsWith(ToxnotReactComponentPaths.BaseSharedMaterialsPath.toLowerCase()) ||
            this.pathMatches(ToxnotReactComponentPaths.CompanyPagesManager) ||
            this.pathMatches(ToxnotReactComponentPaths.MyPassports)) &&
            !path.includes("/start");
    }

    // ===============================================================================

    public renderSignedOutNavLayout(smallNav: boolean) {
        return (
            <nav>
                <ul>
                    <NavMenuReactLink navItemClass={"nav-group"} href={ToxnotReactComponentPaths.BaseSharedMaterialsPath} isActive={this.isExchangeNavMenuItemActive()} iconSrc={"exchange-icon-3.svg"} localizedText={this.localizeString("Passport Library")} lastNavItemInGroup={false} smallNav={smallNav} userRole={TeamRole.Viewer} />
                </ul>
                <hr />
                <ul>
                    <div className="sign-in-not-required-left-nav-buttons-container">
                        <a href="/Account/SignIn" className="btn btn-default sign-in-not-required-left-nav-button"><div className="content">{this.localizeString("Sign In")}</div></a>
                        <a href="/Account/SignUp" className="btn btn-default sign-in-not-required-left-nav-button sign-in-not-required-left-nav-button-margin-top"><div className="content">{this.localizeString("Sign Up For Free")}</div></a>
                    </div>
                </ul>
            </nav>
        );
    }

    // ===============================================================================

    private renderNavLayout(userContext: UserContext, smallNav: boolean, isSideNavCollapsed: boolean) {

        return (
            <div id={"asd"} className="navbar-div-id">
                <nav>
                    {this.renderUserCapabilitiesSideMenuItems(smallNav)}
                    <ul>
                        <NavMenuReactLink navItemClass={"nav-group-standalone"} href={ToxnotReactComponentPaths.StartPath} isActive={this.pathContains(ToxnotReactComponentPaths.StartPath)} iconSrc={"home-icon-3.svg"} localizedText={this.localizeString("Get Started")} lastNavItemInGroup={false} smallNav={smallNav} userRole={userContext.role} />
                        {(this.props.appServices.userContext!.userCapabilities.newsEnabled) &&
                            <NavMenuLink navItemClass={"nav-group-standalone"} href={ToxnotReactComponentPaths.BaseNewsLandingPath} isActive={this.pathContains(ToxnotReactComponentPaths.BaseNewsPath)} iconSrc={"newspaper-icon-3.svg"} localizedText={this.localizeString("News")} lastNavItemInGroup={true} userRole={userContext.role} />
                        }
                    </ul>
                    <hr />
                    <ul>
                        <NavMenuLink navItemClass={"nav-group"} href={""} isActive={false} iconSrc={"product-icon-3.svg"} localizedText={this.localizeString("My Products")} lastNavItemInGroup={false} menuItem_onClick={this.productsMenuItem_onClick} context={"Products"} navGroupId={"library"} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group"} href={""} isActive={false} iconSrc={"materials-icon-3.svg"} localizedText={this.localizeString("My Materials")} lastNavItemInGroup={false} menuItem_onClick={this.materialsMenuItem_onClick} context={"Materials"} navGroupId={"library"} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Certifications/Certifications"} isActive={false} iconSrc={""} localizedText={this.localizeString("Certifications")} lastNavItemInGroup={false} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Compliance/ScipBulkSubmit"} isActive={false} iconSrc={""} localizedText={this.localizeString("SCIP Dashboard")} lastNavItemInGroup={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        {(this.props.appServices.userContext!.userCapabilities.aiAssistantEnabled) &&
                            <NavMenuLink navItemClass={"nav-group-standalone"} href={ToxnotReactComponentPaths.BaseAIAssistantHomePath} isActive={this.pathContains(ToxnotReactComponentPaths.BaseAIAssistantPath)} iconSrc={"portfolio-assistant-3.svg"} localizedText={this.localizeString("Portfolio Assistant ✨")} lastNavItemInGroup={false} userRole={userContext.role} />
                        }
                        <NavMenuLink navItemClass={"nav-group"} href={"/Analytics/Analytics"} isActive={false} iconSrc={"analytics-icon-3.svg"} localizedText={this.localizeString("Portfolio Analytics")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Analytics/DesignTools"} isActive={false} iconSrc={""} localizedText={this.localizeString("3E Exchange Lists")} lastNavItemInGroup={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        {this.renderSharedMaterialsNavMenuGroup(smallNav, userContext.role, isSideNavCollapsed)}
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Publications/Publications"} isActive={false} iconSrc={"publications-icon-3.svg"} localizedText={this.localizeString("My Publications")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Suppliers/Suppliers"} isActive={false} iconSrc={"network-icon-3.svg"} localizedText={this.localizeString("My Suppliers")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        {this.renderSurveyNavMenuGroup(smallNav, userContext.role, isSideNavCollapsed)}
                        <NavMenuReactLink navItemClass={"nav-group-standalone"} href={"/MyCustomers/Browse"} isActive={this.pathContains(ToxnotReactComponentPaths.MyCustomersBasePath)} iconSrc={"customers-icon-4.svg"} localizedText={this.localizeString("My Customers")} lastNavItemInGroup={false} smallNav={smallNav} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Data/Import"} isActive={false} iconSrc={"import-icon-3.svg"} localizedText={this.localizeString("Import Data")} lastNavItemInGroup={false} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Substances/Search"} isActive={false} iconSrc={"search-icon-3.svg"} localizedText={this.localizeString("Chemical Hazards")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group"} href={"/Substances/SubstanceLists"} isActive={false} iconSrc={"list-icon-3.svg"} localizedText={this.localizeString("Lists & Substances")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Analytics/DesignTools"} isActive={false} iconSrc={""} localizedText={this.localizeString("3E Exchange Lists")} lastNavItemInGroup={true} hide={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Data/MyLists"} isActive={false} iconSrc={""} localizedText={this.localizeString("My Lists")} lastNavItemInGroup={true} hide={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Data/ProductPrivateSubstanceData"} isActive={false} iconSrc={""} localizedText={this.localizeString("My Substances")} lastNavItemInGroup={true} hide={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Compliance/SubstanceDataChangeNotifications"} isActive={false} iconSrc={""} localizedText={this.localizeString("Substance Data Changes")} lastNavItemInGroup={true} hide={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        {(this.props.appServices.userContext!.userCapabilities.regulationsEnabled) &&
                            <NavMenuLink navItemClass={"nav-group-standalone"} href={ToxnotReactComponentPaths.BaseRegulationPath} isActive={this.pathContains(ToxnotReactComponentPaths.BaseRegulationPath)} iconSrc={"library-icon-3.svg"} localizedText={this.localizeString("U.S. PFAS Regulations")} lastNavItemInGroup={false} userRole={userContext.role} />
                        }
                        <NavMenuReactLink navItemClass={"nav-group-standalone"} href={"/Tasks"} isActive={this.pathContains(ToxnotReactComponentPaths.TaskManagementPath)} iconSrc={"tasks-icon-3.svg"} localizedText={this.localizeString("Tasks")} lastNavItemInGroup={false} isUpsell={!userContext.userCapabilities.tasksEnabled} smallNav={smallNav} userRole={userContext.role} />
                    </ul>
                    {userContext.role !== TeamRole.Marketing &&
                        <hr />
                    }
                    {userContext.role !== TeamRole.Marketing &&
                        <ul>
                            <NavMenuLink navItemClass={"nav-group-standalone opacity-fix-clipboard-icon"} href={"/Suppliers/SupplierDashboard"} isActive={false} iconSrc={this.props.appServices.userContext?.hasSurveyResponseNotifications ? "clipboard-notifications.svg" : "clipboard.svg"} localizedText={this.localizeString("Fill Out Surveys")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        </ul>
                    }
                    <hr />
                    <ul>
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Data/DataTemplates"} isActive={false} iconSrc={"documents-icon-3.svg"} localizedText={this.localizeString("Templates")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group-standalone"} href={"/Account/Upgrade"} isActive={false} iconSrc={"subscription.svg"} localizedText={this.localizeString("Subscription")} lastNavItemInGroup={false} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />
                        <NavMenuLink navItemClass={"nav-group"} href={"/Settings"} isActive={false} iconSrc={"settings-icon-3.svg"} localizedText={this.localizeString("Settings")} lastNavItemInGroup={false} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Account/Profile"} isActive={false} iconSrc={""} localizedText={this.localizeString("Profile")} lastNavItemInGroup={false} userRole={userContext.role} />
                        <NavMenuLink navItemClass={"nav-group-item"} href={"/Account/Team"} isActive={false} iconSrc={""} localizedText={this.localizeString("Manage Team")} lastNavItemInGroup={true} isUpsell={!userContext.userCapabilities.teamEnabled} userRole={userContext.role} />
                        {userContext.userCapabilities.apiAccessEnabled && <NavMenuLink navItemClass={"nav-group-item"} href={"/Developer"} isActive={false} iconSrc={""} localizedText={this.localizeString("API & Developers")} lastNavItemInGroup={true} userRole={userContext.role} hiddenForRoles={[TeamRole.Marketing]} />}
                        {smallNav ? this.renderSmallSideBarAccountMenu() : this.renderNormalSideBarAccountMenu(userContext)}
                    </ul>
                </nav>
            </div>
        );
    }

    // ===============================================================================

    public renderNormalSideBarAccountMenu(userContext: UserContext) {
        const showTeamName = userContext.userCapabilities.teamEnabled ||
            (userContext.availableTeamsList.length > 1);

        return (
            <li id="sidebar-account-menu-container" className="collapsed">
                <a data-placement="right" data-toggle="tooltip" data-container=".sidebar-line" data-html="true" title={showTeamName ? userContext.emailAddress + "<br/>" + userContext.teamName : userContext.emailAddress} id="sidebar-account-menu-button" role="button" onClick={e => this.sidebarAccountMenuButton_click()}>
                    <div className="icon">
                        <img src={"/images/nav/gray/account-icon-3.svg"} />
                    </div>
                    <div className="content">
                        {userContext.emailAddress}
                        {showTeamName && (
                            <React.Fragment>
                                <br />
                                {userContext.teamName}
                            </React.Fragment>
                        )}
                    </div>
                </a>
            </li>
        );
    }

    // ===============================================================================

    public renderSmallSideBarAccountMenu() {
        return (
            <li className="nav-group-standalone">
                <a data-placement="right" data-toggle="tooltip" data-container=".sidebar-line" title={this.localizeString("Sign Out")} role="button" className="sign-out-button" onClick={e => window.location.href = "/Account/SignIn?logout=True"}>
                    <div className="icon"></div>
                    <div className="content">{this.localizeString("Sign Out")}</div>
                </a>
            </li >
        );
    }

    // ===============================================================================

    private renderSharedMaterialsNavMenuGroup(smallNav: boolean, role: TeamRole, isSideNavCollapsed: boolean) {
        const path = window.location.pathname.toLowerCase();

        const hideExchangeSubItems = !path.startsWith(ToxnotReactComponentPaths.BaseSharedMaterialsPath.toLowerCase());

        return (
            <>
                <NavMenuReactLink navGroupId={"shared-materials"} navItemClass={"nav-group-standalone"} href={ToxnotReactComponentPaths.BaseSharedMaterialsPath} isActive={this.isExchangeNavMenuItemActive()} iconSrc={"exchange-icon-3.svg"} localizedText={this.localizeString("Passport Library")} lastNavItemInGroup={false} smallNav={smallNav} userRole={role} />
                <NavMenuReactLink isSideNavCollapsed={isSideNavCollapsed} navGroupId={"shared-materials"} navItemClass={"nav-group-item"} href={ToxnotReactComponentPaths.CompanyPagesManager} isActiveParent={this.isAnyExchangeNavMenuItemActive()} isActive={this.pathMatches(ToxnotReactComponentPaths.CompanyPagesManager)} iconSrc={""} localizedText={this.localizeString("My Brands")} lastNavItemInGroup={false} smallNav={smallNav} hide={hideExchangeSubItems} userRole={role} />
                <NavMenuReactLink isSideNavCollapsed={isSideNavCollapsed} navGroupId={"shared-materials"} navItemClass={"nav-group-item"} href={ToxnotReactComponentPaths.MyPassports} isActiveParent={this.isAnyExchangeNavMenuItemActive()} isActive={this.pathMatches(ToxnotReactComponentPaths.MyPassports)} iconSrc={""} localizedText={this.localizeString("My Passports")} lastNavItemInGroup={true} smallNav={smallNav} hide={hideExchangeSubItems} userRole={role} />
            </>
        );
    }

    // ===============================================================================

    private renderSurveyNavMenuGroup(smallNav: boolean, role: TeamRole, isSideNavCollapsed: boolean) {
        if (role === TeamRole.Marketing) {
            return null;
        }

        const userCapabilities = this.props.appServices.userContext!.userCapabilities;
        const hasObtainmentReports = userCapabilities.obtainmentReportsEnabled;
        const mySurveysIsActive = this.pathContains("Surveys/SupplierSurveys");
        const questionnairesIsActive = this.pathContains("Surveys/Questionnaires");
        const campaignsIsActive = this.pathMatches(CreateAndEditSurveyComponentPaths.SurveyCampaignsPath);
        const obtainmentIsActive = this.pathContains("ObtainmentReports");
        const anySurveySubItemIsActive = questionnairesIsActive || campaignsIsActive || obtainmentIsActive;
        const hideSubItems = !mySurveysIsActive && !anySurveySubItemIsActive;

        return (
            <>
                <NavMenuLink navGroupId={"supplier-surveys"} navItemClass={"nav-group-standalone"} href={"/Surveys/SupplierSurveys"} isActive={mySurveysIsActive} iconSrc={"surveys-icon-3.svg"} localizedText={this.localizeString("My Surveys")} lastNavItemInGroup={false} userRole={role} />
                <NavMenuLink isSideNavCollapsed={isSideNavCollapsed} navGroupId={"supplier-surveys"} navItemClass={"nav-group-item"} href={"/Surveys/Questionnaires"} isActive={questionnairesIsActive} iconSrc={""} localizedText={this.localizeString("Questionnaires")} lastNavItemInGroup={false} hide={hideSubItems} userRole={role} isActiveParent={anySurveySubItemIsActive} />
                <NavMenuReactLink isSideNavCollapsed={isSideNavCollapsed} navGroupId={"supplier-surveys"} navItemClass={"nav-group-item"} href={CreateAndEditSurveyComponentPaths.SurveyCampaignsPath} isActive={campaignsIsActive} iconSrc={""} localizedText={this.localizeString("My Campaigns")} lastNavItemInGroup={!hasObtainmentReports} hide={hideSubItems} smallNav={smallNav} userRole={role} isActiveParent={anySurveySubItemIsActive} />
                {hasObtainmentReports &&
                    <NavMenuLink isSideNavCollapsed={isSideNavCollapsed} navGroupId={"supplier-surveys"} navItemClass={"nav-group-item"} href={ToxnotReactComponentPaths.ObtainmentReportsPath} isActive={obtainmentIsActive} isActiveParent={anySurveySubItemIsActive} iconSrc={""} localizedText={this.localizeString("Obtainment Reports")} lastNavItemInGroup={true} hide={hideSubItems} userRole={role} />
                }
            </>
        );
    }

    // ===============================================================================

    private renderTeamMenu(userContext: UserContext) {
        const menuItems: JSX.Element[] = [];
        const teamItems: JSX.Element[] = [];
        let itemIndex = 0;

        const getTeamName = (team: AvailableTeam) => team.teamName === "" || team.teamName === null ? "Unnamed Team" : team.teamName;

        if (userContext.availableTeamsList.length > 0) {
            for (let availableTeam of userContext.availableTeamsList.orderBy(team => getTeamName(team))) {
                teamItems.push(
                    <li key={itemIndex++} >
                        <a href={"/Account/ActivateTeam?teamOwnerId=" + availableTeam.teamOwnerId} className={"team-selector" + (availableTeam.isActive ? " active-team" : "")}>
                            {getTeamName(availableTeam) + (availableTeam.isActive ? " \u2666" : "")}
                        </a>
                    </ li>
                );
            }
        }

        if (userContext.isToxnotAdminUser) {
            if (userContext.availableTeamsList.length > 0) {
                menuItems.push(<li key={itemIndex++} role="separator" className="divider"></li>);
            }
            menuItems.push(<li key={itemIndex++}><a href={"/Admin/Admin"}>Admin</a></li>);
            menuItems.push(<li key={itemIndex++}><a href={"/Admin/DesignLanguage"}>Design Language</a></li>);
        }
        menuItems.push(<li key={itemIndex++} role="separator" className="divider"></li>);
        menuItems.push(<li key={itemIndex++}><a href={"/Account/SignIn?logout=True"}>{this.localizeString("Sign Out")}</a></li>);
        return (
            <ul>
                <li id="teams-scrollable-list-item">
                    <ul>
                        {teamItems}
                    </ul>
                </li>
                {menuItems}
            </ul>
        );
    }

    // ===============================================================================

    private ifRenderAnyTopLevelSideMenuBarItems() {
        const userCapabilities = this.props.appServices.userContext!.userCapabilities;
        return userCapabilities.consultantAccountEnabled ||
            userCapabilities.declareAdministrationEnabled ||
            userCapabilities.submissionPlatformPartnerEnabled ||
            userCapabilities.myGreenLabBackendEnabled;
    }

    // ===============================================================================

    private renderUserCapabilitiesSideMenuItems(isSmallNav: boolean) {
        const shouldRender = this.ifRenderAnyTopLevelSideMenuBarItems() && (isSmallNav || !this.state.isSideNavCollapsed);

        if (!shouldRender) {
            return null;
        }

        const userCapabilities = this.props.appServices.userContext!.userCapabilities;
        return (
            <React.Fragment>
                <ul className={isSmallNav ? "user-capabilities-list-sm" : "user-capabilities-list"}>
                    {userCapabilities.consultantAccountEnabled && <li className="nav-group-standalone"><a href="/Consultant">{this.localizeString("Consultant Dashboard")}</a></li>}
                    {userCapabilities.declareAdministrationEnabled && <li className="nav-group-standalone" nav-group-id="declare-admin"><a href="/Ilfi/DeclareAdmin">{this.localizeString("Declare Admin")}</a></li>}
                    {userCapabilities.submissionPlatformPartnerEnabled && <li className="nav-group-standalone"><a href="/Partner/Dashboard">{this.localizeString("Submissions Admin")}</a></li>}
                    {userCapabilities.myGreenLabBackendEnabled && <li className="nav-group-standalone"><a href="/MyGreenLab">{this.localizeString("MyGreenLab Dashboard")}</a></li>}
                </ul>
                <hr />
            </React.Fragment>
        );
    }

    // ===============================================================================

    private pathMatches(path: string) {
        return path.toLowerCase().trim() === window.location.pathname.toLowerCase().trim();
    }

    // ===============================================================================

    private pathContains(path: string) {
        return window.location.pathname.toLowerCase().trim().includes(path.toLowerCase().trim());
    }

    // ===============================================================================

    private navigateToLibaray = (context: string) => {
        localStorage.setItem("library-products-materials-mode", context);
        window.location.href = "/Data/Library";
    }

    // ===============================================================================
    //
    //  UI Event Handlers
    //
    // ===============================================================================

    private sidebarAccountMenuButton_click = () => {
        const $menuContainer = $("#account-menu-content");

        const newState = $menuContainer.hasClass("collapsed") ? "open" : "collapsed";

        const $menuButton = $("#sidebar-account-menu-container");

        const menuButtonOffset = $menuButton.offset();

        const $fixedDiv = $("#account-menu-content > div");
        const $menu = $fixedDiv.find("ul");

        $menuContainer.removeClass("collapsed").removeClass("open").addClass(newState);

        $fixedDiv.css({
            "top": (menuButtonOffset!.top + $menuButton!.outerHeight()! - $menu!.outerHeight()!).toString() + "px",
            "left": (menuButtonOffset!.left + $menuButton!.outerWidth()!).toString() + "px"
        });
    }

    // ===============================================================================

    private smallNavExpandButton_click = () => {
        $("#tn-app-small-navigation-container").slideToggle();
    }

    // ===============================================================================

    private productsMenuItem_onClick = (e: React.MouseEvent): void => {
        this.navigateToLibaray("products");
    }

    // ===============================================================================

    private materialsMenuItem_onClick = (e: React.MouseEvent): void => {
        this.navigateToLibaray("materials");
    }

    // ===============================================================================

    private tow_decision_modal_ok_onClick = (ev: React.MouseEvent) => {
        this.props.towTaskDelete(this.props.appServices.towState!);
    }

    // ===============================================================================

    private navbarCollapse = (isCollapseAction: boolean) => {
        this.setState({
            isSideNavCollapsed: isCollapseAction
        });
        if (isCollapseAction) {
            $('.tn-app-sidebar').addClass('collapsed-navbar side-nav-transition');
            $('.btn-collapse-nav').addClass('initially-hidden');
            $('.btn-expand-nav').removeClass('initially-hidden');
        } else {
            $('.tn-app-sidebar').removeClass('collapsed-navbar side-nav-transition');
            $('.btn-expand-nav').addClass('initially-hidden');
            $('.btn-collapse-nav').removeClass('initially-hidden');
        }
        this.setCookie('toxnot-side-navbar-collapsed', isCollapseAction.toString());
    }

    // ===============================================================================

    private navbarKeysEventListener(e: any, navbarCollapse: any) {
        if (document.activeElement?.tagName === 'INPUT') return;

        if (e.key === "]") navbarCollapse(false);
        if (e.key === "[") navbarCollapse(true);
    }

    // ===============================================================================

    private setCookie(name: string, value: string) {
        const date = new Date();
        date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));

        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value || ""}; ${expires}; path=/`;
    }

    // ===============================================================================

    private getCookie(name: any): string | undefined {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);

        if (parts.length === 2) {
            return parts.pop()?.split(';').shift();
        }
    }

    // ===============================================================================

    private isTruthyCookie(cookieName: string | undefined): boolean {
        const cookieVal = this.getCookie(cookieName);

        return cookieVal === undefined || cookieVal === null ? false : cookieVal.toLowerCase() === 'true';
    }

    private getBannerStyle(): React.CSSProperties {
        const style: React.CSSProperties = {
            backgroundColor: '#0e4876',
            color: 'white',
            textAlign: 'center',
            padding: '5px',
            justifyContent: 'space-around',
            position: 'sticky',
            top: '0',
            zIndex: 9999,
            width: '100%',
            marginBottom: '10px'
        };

        if (this.state.isRefreshImminent) {
            style.backgroundColor = 'darkred';
        }

        return style;
    }

    // ===============================================================================

    private updateCountdown = () => {
        if (this.props.appServices.config.dataRefreshStartDate) {
            const startDate = new Date(this.props.appServices.config.dataRefreshStartDate);
            const now = new Date();
            const twoWeeksInMillis = 14 * 24 * 60 * 60 * 1000;
            const twoHoursInMillis = 2 * 60 * 60 * 1000;

            let nextRefreshDate = new Date(startDate.getTime());
            while (nextRefreshDate < now) {
                nextRefreshDate = new Date(nextRefreshDate.getTime() + twoWeeksInMillis);
            }

            const distance = nextRefreshDate.getTime() - now.getTime();

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            this.setState({
                countdown: `${days}d ${hours}h ${minutes}m ${seconds}s`,
                isRefreshImminent: distance < twoHoursInMillis
            });
        }
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState, ownProps: LayoutOwnProps) => ({ appServices: state.appServices }),
    LoginStateController.actionCreators
)(Layout as any);
